import { useState } from "react";
import { Mail, MessageSquare, ShieldAlert, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TOPICS = [
  { value: "bug", label: "I found a bug", icon: ShieldAlert },
  { value: "feature", label: "I have a feature idea", icon: MessageSquare },
  { value: "safety", label: "Safety or moderation concern", icon: ShieldAlert },
  { value: "billing", label: "Payment / billing question", icon: Mail },
  { value: "account", label: "Account or login help", icon: Mail },
  { value: "other", label: "Something else", icon: Mail },
];

export default function SupportContact() {
  const { user } = useAuth();
  const [topic, setTopic] = useState("bug");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);

  const canSubmit = subject.trim().length >= 3 && body.trim().length >= 10 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      // Route through existing feedback endpoint — same audit trail, reviewed
      // by admins via /admin/feedback. Topic and email captured in the body.
      const composedBody = [
        `Topic: ${TOPICS.find((t) => t.value === topic)?.label}`,
        `Subject: ${subject.trim()}`,
        `Reply-to: ${email || "(not provided)"}`,
        "",
        body.trim(),
      ].join("\n");
      await api.post("/feedback", {
        type: "other",
        description: composedBody,
        page_url: "/contact",
      });
      toast.success("Thanks — we got your message and will reply within one business day.");
      setSubject("");
      setBody("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SiteShell>
      <section className="container-page py-14" data-testid="support-contact">
        <span className="chip inline-flex items-center gap-1.5">Support</span>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight">Get in touch</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">
          A real human reads every message. Whether you've found a bug, want to suggest a
          feature, need account help, or want to report unsafe behaviour, we're here for it.
        </p>

        <div className="mt-10 grid lg:grid-cols-[1fr_360px] gap-10">
          <form onSubmit={submit} className="card-soft space-y-5" data-testid="support-contact-form">
            <div>
              <label className="text-sm font-medium mb-2 block">What's it about?</label>
              <div className="grid grid-cols-2 gap-2">
                {TOPICS.map((t) => {
                  const active = topic === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setTopic(t.value)}
                      data-testid={`support-topic-${t.value}`}
                      className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors ${
                        active
                          ? "border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-moss))]/60 text-[hsl(var(--sb-forest-2))]"
                          : "hover:bg-muted/40"
                      }`}
                    >
                      <t.icon size={14} /> <span className="truncate">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label htmlFor="support-subject" className="text-sm font-medium mb-1.5 block">
                Subject
              </label>
              <input
                id="support-subject"
                className="sb-input"
                placeholder="One-line summary"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                minLength={3}
                maxLength={140}
                data-testid="support-subject"
              />
            </div>

            <div>
              <label htmlFor="support-body" className="text-sm font-medium mb-1.5 block">
                Details
              </label>
              <textarea
                id="support-body"
                rows={6}
                className="sb-input"
                placeholder="What happened? What did you expect? If it's a safety issue, please include the other user's display name."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
                minLength={10}
                maxLength={8000}
                data-testid="support-body"
              />
            </div>

            <div>
              <label htmlFor="support-email" className="text-sm font-medium mb-1.5 block">
                Reply-to email {user && <span className="text-xs text-muted-foreground">(optional if you're signed in)</span>}
              </label>
              <input
                id="support-email"
                type="email"
                className="sb-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="support-email"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="btn-primary"
                disabled={!canSubmit}
                data-testid="support-submit"
              >
                <Send size={14} /> {busy ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>

          <aside className="space-y-5">
            <div className="card-soft">
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Direct email
              </div>
              <a
                href="mailto:hello@lastbookmark.com"
                className="mt-2 inline-flex items-center gap-2 font-serif text-2xl link-underline"
                data-testid="support-mailto"
              >
                <Mail size={20} /> hello@lastbookmark.com
              </a>
              <p className="mt-3 text-sm text-muted-foreground">
                We reply within one business day. For urgent safety issues, prefix your subject
                line with <span className="font-mono text-xs">[URGENT]</span>.
              </p>
            </div>

            <div className="card-soft">
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Before you write
              </div>
              <ul className="mt-2 space-y-2 text-sm">
                <li>
                  <a href="/beta-terms" className="link-underline inline-flex items-center gap-1">
                    Beta Terms <ExternalLink size={11} />
                  </a>
                </li>
                <li>
                  <a href="/payment-guidance" className="link-underline inline-flex items-center gap-1">
                    Safe payments during beta <ExternalLink size={11} />
                  </a>
                </li>
                <li>
                  <a href="/community-guidelines" className="link-underline inline-flex items-center gap-1">
                    Community Guidelines <ExternalLink size={11} />
                  </a>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </SiteShell>
  );
}
